package co.maytes.standupbot.dashboard.pages.app.releases

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.ErrorState
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.motion.Revalidating
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.motion.ViewPhase
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideSearch
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideSettings
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideTag
import co.maytes.standupbot.dashboard.features.releases.FeedBarActions
import co.maytes.standupbot.dashboard.features.releases.FeedBarState
import co.maytes.standupbot.dashboard.features.releases.FeedClasses
import co.maytes.standupbot.dashboard.features.releases.FeedDensity
import co.maytes.standupbot.dashboard.features.releases.FeedListActions
import co.maytes.standupbot.dashboard.features.releases.FeedListState
import co.maytes.standupbot.dashboard.features.releases.FeedSubtitle
import co.maytes.standupbot.dashboard.features.releases.LatestReleasesStrip
import co.maytes.standupbot.dashboard.features.releases.ReleaseEmptyState
import co.maytes.standupbot.dashboard.features.releases.ReleaseFeed
import co.maytes.standupbot.dashboard.features.releases.ReleaseFeedBar
import co.maytes.standupbot.dashboard.features.releases.ReleaseFeedGroups
import co.maytes.standupbot.dashboard.features.releases.ReleaseFeedList
import co.maytes.standupbot.dashboard.features.releases.ReleaseFeedSkeleton
import co.maytes.standupbot.dashboard.features.releases.ReleaseFeedUrlState
import co.maytes.standupbot.dashboard.features.releases.ReleaseFeedView
import co.maytes.standupbot.dashboard.features.releases.ReleaseFilters
import co.maytes.standupbot.dashboard.features.releases.ReleaseZoneChoice
import co.maytes.standupbot.dashboard.features.releases.ReleasesContainer
import co.maytes.standupbot.dashboard.features.releases.ReleasesContract
import co.maytes.standupbot.dashboard.features.releases.releaseEmptyState
import co.maytes.standupbot.dashboard.features.releases.resolveReleaseZone
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Layout
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import co.maytes.standupbot.shared.format.ReleaseTimeFormat
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.web.dom.Div

private const val ZONE_STORAGE_KEY = "sb-releases-zone"
private const val DENSITY_STORAGE_KEY = "sb-releases-density"
private const val EMPTY_ICON_SIZE = 26
private const val AUTO_LOAD_LIMIT = 2000

@Page("/app/releases")
@Composable
fun ReleasesIndexPage() =
    AuthGuard {
        AppShell {
            ReleasesScreen()
        }
    }

@Composable
private fun ReleasesScreen() {
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val container = remember(api) { ReleasesContainer(scope, api) }

    val state by container.subscribe { action ->
        when (action) {
            is ReleasesContract.Action.Navigate -> ctx.router.navigateTo(action.path)
        }
    }

    val workspaceId = auth.currentUser?.workspace?.id
    val role = auth.currentUser?.role
    val isAdmin = role == RoleDto.OWNER || role == RoleDto.ADMIN
    val initialView = remember { readUrlView() }
    var filters by remember { mutableStateOf(initialView.filters) }
    var grouping by remember { mutableStateOf(initialView.grouping) }
    var density by remember { mutableStateOf(readStoredDensity()) }
    var zoneChoice by remember { mutableStateOf(readStoredZoneChoice()) }
    var expandedIds by remember { mutableStateOf(emptySet<String>()) }
    var repoOverrides by remember { mutableStateOf(emptyMap<String, Boolean>()) }

    LaunchedEffect(workspaceId, isAdmin) {
        if (!workspaceId.isNullOrBlank()) container.intent(ReleasesContract.Intent.Load(workspaceId, isAdmin, filters.serverRepo))
    }
    LaunchedEffect(filters.serverRepo, state.workspaceId) {
        if (state.workspaceId != null && filters.serverRepo != state.repo) {
            container.intent(ReleasesContract.Intent.SetRepo(filters.serverRepo))
        }
    }
    LaunchedEffect(state.status, state.pageInfo?.nextCursor, state.loadMoreError) {
        val canAutoLoad = state.status == ReleasesContract.Status.Loaded && state.loadMoreError == null
        if (canAutoLoad && state.pageInfo?.hasMore == true && state.items.size < AUTO_LOAD_LIMIT) {
            container.intent(ReleasesContract.Intent.LoadMore)
        }
    }
    LaunchedEffect(filters, grouping) { writeUrlView(ReleaseFeedView(filters, grouping)) }

    val zone = resolveReleaseZone(zoneChoice, auth.currentUser?.workspace?.defaultTimezone)
    val now = remember(state.items) { Clock.System.now() }
    val today = ReleaseTimeFormat.localDay(now, zone)
    val hasMore = state.pageInfo?.hasMore == true
    val visible = remember(state.items, filters, now) { ReleaseFeed.filter(state.items, filters, now) }
    val showsFeed = state.showsRows && state.items.isNotEmpty()
    val showBar = state.phase == ViewPhase.Loading || showsFeed || (state.showsRows && filters.isActive)

    val subtitle =
        if (state.showsRows) {
            remember(state.items, now, hasMore, zone) { buildSubtitle(state.items, now, hasMore, zone) }
        } else {
            null
        }

    Div(attrs = Modifier.classNames(FeedClasses.PAGE).fillMaxWidth().maxWidth(Layout.wide).toAttrs()) {
        PageHeader(subtitle = subtitle, isAdmin = isAdmin, onSettings = { container.intent(ReleasesContract.Intent.GoToSettings) })
        if (showsFeed) {
            LatestReleasesStrip(releases = ReleaseFeed.latestReleases(state.items), now = now, zone = zone)
        }
        if (showBar) {
            val loaded = state.showsRows
            ReleaseFeedBar(
                state =
                    FeedBarState(
                        filters = filters,
                        kindCounts = if (loaded) ReleaseFeed.kindCounts(state.items, filters, now) else null,
                        repoOptions = ReleaseFeed.repoOptions(state.items, state.knownRepos, filters, now, state.repo),
                        grouping = grouping,
                        density = density,
                        zoneChoice = zoneChoice,
                        resultText =
                            if (loaded) ReleaseFeed.resultCount(visible.size, state.items.size, hasMore, filters.isActive) else null,
                        partialNote = if (loaded) ReleaseFeed.partialFilterNote(state.items, filters, now, hasMore) else null,
                    ),
                actions =
                    FeedBarActions(
                        onFilters = { filters = it },
                        onGrouping = { grouping = it },
                        onDensity = {
                            density = it
                            writeStoredDensity(it)
                        },
                        onZone = {
                            zoneChoice = it
                            writeStoredZoneChoice(it)
                        },
                    ),
            )
        }
        Div(attrs = Modifier.fillMaxWidth().margin(top = Spacing.sm).toAttrs()) {
            ReleasesBody(
                state = state,
                filters = filters,
                visible = visible,
                isAdmin = isAdmin,
                list =
                    FeedListState(
                        events = visible,
                        grouping = grouping,
                        density = density,
                        zone = zone,
                        now = now,
                        today = today,
                        searching = filters.query.isNotBlank(),
                        expandedIds = expandedIds,
                        repoOverrides = repoOverrides,
                    ),
                listActions =
                    FeedListActions(
                        onToggleRow = { id -> expandedIds = if (id in expandedIds) expandedIds - id else expandedIds + id },
                        onToggleRepo = { repo, open -> repoOverrides = repoOverrides + (repo to open) },
                        onSetAllRepos = { repos, open -> repoOverrides = repos.associateWith { open } },
                    ),
                onClearFilters = { filters = ReleaseFilters() },
                dispatch = { container.intent(it) },
            )
        }
    }
}

private fun buildSubtitle(
    items: List<ShipEventDto>,
    now: Instant,
    hasMore: Boolean,
    zone: TimeZone,
): FeedSubtitle? {
    val newest = items.maxOfOrNull { it.occurredAt } ?: return null
    val relative = ReleaseFeedGroups.relativeInSentence(ReleaseFeedGroups.relative(now, newest, zone))
    return ReleaseFeed.subtitle(items = items, now = now, hasMore = hasMore, relativeLatest = relative)
}

@Composable
private fun PageHeader(
    subtitle: FeedSubtitle?,
    isAdmin: Boolean,
    onSettings: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.lg),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column {
            SpanText(
                text = "Releases",
                modifier =
                    Modifier
                        .fontSize(Typography.h2.size)
                        .fontWeight(Typography.h2.weight)
                        .color(Palette.textPrimary),
            )
            Div(
                attrs =
                    Modifier
                        .margin(top = Spacing.xxs)
                        .fontSize(Typography.body.size)
                        .color(Palette.textSecondary)
                        .styleModifier { property("min-height", "calc(${Typography.body.size} * 1.6)") }
                        .toAttrs(),
            ) {
                if (subtitle != null) {
                    SpanText(
                        text = subtitle.strong,
                        modifier = Modifier.fontWeight(Typography.h4.weight).color(Palette.textPrimary),
                    )
                    SpanText(text = subtitle.tail)
                }
            }
        }
        if (isAdmin) {
            Button(
                label = "Release settings",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                leadingIcon = { LucideSettings(size = SETTINGS_ICON_SIZE) },
                onClick = onSettings,
            )
        }
    }
}

private const val SETTINGS_ICON_SIZE = 16

@Composable
private fun ReleasesBody(
    state: ReleasesContract.State,
    filters: ReleaseFilters,
    visible: List<ShipEventDto>,
    isAdmin: Boolean,
    list: FeedListState,
    listActions: FeedListActions,
    onClearFilters: () -> Unit,
    dispatch: (ReleasesContract.Intent) -> Unit,
) {
    StateSwap(state = state.phase) {
        when (val status = state.status) {
            ReleasesContract.Status.Idle, ReleasesContract.Status.Loading -> ReleaseFeedSkeleton()
            is ReleasesContract.Status.Error ->
                ErrorState(
                    message = status.error.displayMessage,
                    onRetry = { state.workspaceId?.let { dispatch(ReleasesContract.Intent.Load(it, isAdmin)) } },
                )
            ReleasesContract.Status.Loaded,
            ReleasesContract.Status.LoadingMore,
            ReleasesContract.Status.Refreshing,
            ->
                Revalidating(refreshing = status == ReleasesContract.Status.Refreshing) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        when {
                            state.isEmpty && state.repo == null && !filters.isActive -> EmptyReleases(isAdmin, state, dispatch)
                            visible.isEmpty() -> NoMatches(state, onClearFilters, dispatch)
                            else -> {
                                ReleaseFeedList(list, listActions)
                                LoadMoreFooter(state, dispatch)
                            }
                        }
                    }
                }
        }
    }
}

@Composable
private fun NoMatches(
    state: ReleasesContract.State,
    onClearFilters: () -> Unit,
    dispatch: (ReleasesContract.Intent) -> Unit,
) {
    val hasMore = state.pageInfo?.hasMore == true
    EmptyState(
        title = "No changes match",
        subtitle =
            if (hasMore) {
                "Nothing in the ${state.items.size} changes loaded so far matches these filters. The rest is still loading."
            } else {
                "Nothing matches these filters. Try a wider time range or fewer filters."
            },
        icon = { LucideSearch(size = EMPTY_ICON_SIZE) },
        ctaLabel = "Clear filters",
        onCta = onClearFilters,
    )
    if (hasMore) LoadMoreFooter(state, dispatch)
}

@Composable
private fun LoadMoreFooter(
    state: ReleasesContract.State,
    dispatch: (ReleasesContract.Intent) -> Unit,
) {
    val count = state.items.size
    val noun = ReleaseFeed.plural(count, "change")
    Column(
        modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg),
        verticalArrangement = Arrangement.spacedBy(Spacing.xs),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        state.loadMoreError?.let {
            SpanText(
                text = "Could not load more: ${it.displayMessage}",
                modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
            )
        }
        SpanText(
            text =
                when {
                    state.pageInfo?.hasMore != true -> "End of the feed, $count $noun"
                    state.loadMoreError == null && count < AUTO_LOAD_LIMIT -> "Loading the rest, $count $noun so far"
                    else -> "$count $noun loaded"
                },
            modifier =
                Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier { property("font-variant-numeric", "tabular-nums") },
        )
        if (state.pageInfo?.hasMore == true && (state.loadMoreError != null || state.items.size >= AUTO_LOAD_LIMIT)) {
            Button(
                label =
                    when {
                        state.status == ReleasesContract.Status.LoadingMore -> "Loading…"
                        state.loadMoreError != null -> "Try again"
                        else -> "Load more"
                    },
                variant = ButtonVariant.Secondary,
                enabled = state.status != ReleasesContract.Status.LoadingMore,
                onClick = { dispatch(ReleasesContract.Intent.LoadMore) },
            )
        }
    }
}

@Composable
private fun EmptyReleases(
    isAdmin: Boolean,
    state: ReleasesContract.State,
    dispatch: (ReleasesContract.Intent) -> Unit,
) {
    when (releaseEmptyState(isAdmin, state.settings, state.github)) {
        ReleaseEmptyState.NoGithub ->
            EmptyState(
                title = "No GitHub repositories connected",
                subtitle = "Connect GitHub and pick the repositories to follow, then merged changes and releases show up here.",
                icon = { LucideTag(size = EMPTY_ICON_SIZE) },
                ctaLabel = "Open integrations",
                onCta = { dispatch(ReleasesContract.Intent.GoToIntegrations) },
            )
        ReleaseEmptyState.TurnOn ->
            EmptyState(
                title = "Turn on the release feed",
                subtitle = "Pick the repositories to follow and the feed fills with what your engineers merge.",
                icon = { LucideTag(size = EMPTY_ICON_SIZE) },
                ctaLabel = "Open release settings",
                onCta = { dispatch(ReleasesContract.Intent.GoToSettings) },
            )
        ReleaseEmptyState.NothingYet ->
            EmptyState(
                title = "Nothing merged or released yet",
                subtitle =
                    "New merges and releases in the tracked repositories appear here within seconds. " +
                        "Use backfill in settings to load the last 30 days.",
                icon = { LucideTag(size = EMPTY_ICON_SIZE) },
                ctaLabel = "Open release settings",
                onCta = { dispatch(ReleasesContract.Intent.GoToSettings) },
            )
        ReleaseEmptyState.AskAdmin ->
            EmptyState(
                title = "Nothing here yet",
                subtitle = "If you expected updates, ask a workspace admin to turn on the release feed.",
                icon = { LucideTag(size = EMPTY_ICON_SIZE) },
            )
    }
}

private fun readUrlView(): ReleaseFeedView =
    try {
        ReleaseFeedUrlState.decode(window.location.search)
    } catch (_: Throwable) {
        ReleaseFeedView()
    }

private fun writeUrlView(view: ReleaseFeedView) {
    try {
        window.history.replaceState(null, "", window.location.pathname + ReleaseFeedUrlState.encode(view))
    } catch (_: Throwable) {
    }
}

private fun readStoredZoneChoice(): ReleaseZoneChoice =
    try {
        ReleaseZoneChoice.entries.firstOrNull { it.name == localStorage.getItem(ZONE_STORAGE_KEY) } ?: ReleaseZoneChoice.Workspace
    } catch (_: Throwable) {
        ReleaseZoneChoice.Workspace
    }

private fun writeStoredZoneChoice(choice: ReleaseZoneChoice) {
    try {
        localStorage.setItem(ZONE_STORAGE_KEY, choice.name)
    } catch (_: Throwable) {
    }
}

private fun readStoredDensity(): FeedDensity =
    try {
        FeedDensity.entries.firstOrNull { it.name == localStorage.getItem(DENSITY_STORAGE_KEY) } ?: FeedDensity.Comfortable
    } catch (_: Throwable) {
        FeedDensity.Comfortable
    }

private fun writeStoredDensity(density: FeedDensity) {
    try {
        localStorage.setItem(DENSITY_STORAGE_KEY, density.name)
    } catch (_: Throwable) {
    }
}
